// Local storage keys
export const STORAGE_KEYS = {
  USER: 'leadgen_user',
  AUTH_TOKEN: 'leadgen_auth_token',
  USER_PREFERENCES: 'leadgen_user_preferences',
  THEME: 'leadgen_theme',
  UNSAVED_SEARCH_RESULTS: 'leadgen_unsaved_search_results',
};

// Safe localStorage operations with error handling
class StorageService {
  // Check if localStorage is available
  isStorageAvailable() {
    try {
      const test = '__localStorage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch {
      return false;
    }
  }

  // Set item in localStorage
  setItem(key, value) {
    if (!this.isStorageAvailable()) {
      console.warn('localStorage is not available');
      return false;
    }

    try {
      const serializedValue = JSON.stringify(value);
      localStorage.setItem(key, serializedValue);
      return true;
    } catch (error) {
      console.error('Error setting localStorage item:', error);
      return false;
    }
  }

  // Get item from localStorage
  getItem(key, defaultValue = null) {
    if (!this.isStorageAvailable()) {
      console.warn('localStorage is not available');
      return defaultValue;
    }

    try {
      const item = localStorage.getItem(key);
      if (item === null) return defaultValue;
      return JSON.parse(item);
    } catch (error) {
      console.error('Error getting localStorage item:', error);
      return defaultValue;
    }
  }

  // Remove item from localStorage
  removeItem(key) {
    if (!this.isStorageAvailable()) {
      console.warn('localStorage is not available');
      return false;
    }

    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.error('Error removing localStorage item:', error);
      return false;
    }
  }

  // Clear all localStorage
  clear() {
    if (!this.isStorageAvailable()) {
      console.warn('localStorage is not available');
      return false;
    }

    try {
      localStorage.clear();
      return true;
    } catch (error) {
      console.error('Error clearing localStorage:', error);
      return false;
    }
  }

  // User-specific methods
  setUser(user) {
    const userData = {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName,
      photoURL: user.photoURL,
      emailVerified: user.emailVerified,
      isAnonymous: user.isAnonymous,
      userType: user.userType || 'standard',
      lastUpdated: Date.now()
    };
    return this.setItem(STORAGE_KEYS.USER, userData);
  }

  getUser() {
    return this.getItem(STORAGE_KEYS.USER);
  }

  removeUser() {
    return this.removeItem(STORAGE_KEYS.USER);
  }

  // Auth token methods
  setAuthToken(token) {
    return this.setItem(STORAGE_KEYS.AUTH_TOKEN, {
      token,
      timestamp: Date.now()
    });
  }

  getAuthToken() {
    const tokenData = this.getItem(STORAGE_KEYS.AUTH_TOKEN);
    if (!tokenData) return null;

    // Check if token is older than 1 hour (Firebase tokens expire in 1 hour)
    const oneHour = 60 * 60 * 1000;
    if (Date.now() - tokenData.timestamp > oneHour) {
      this.removeAuthToken();
      return null;
    }

    return tokenData.token;
  }

  removeAuthToken() {
    return this.removeItem(STORAGE_KEYS.AUTH_TOKEN);
  }

  // User preferences methods
  setUserPreferences(preferences) {
    return this.setItem(STORAGE_KEYS.USER_PREFERENCES, preferences);
  }

  getUserPreferences() {
    return this.getItem(STORAGE_KEYS.USER_PREFERENCES, {
      theme: 'light',
      notifications: true,
      language: 'en'
    });
  }

  // Theme methods
  setTheme(theme) {
    return this.setItem(STORAGE_KEYS.THEME, theme);
  }

  getTheme() {
    return this.getItem(STORAGE_KEYS.THEME, 'light');
  }

  // Clear all auth-related data
  clearAuthData() {
    this.removeUser();
    this.removeAuthToken();
    return true;
  }

  // Get all stored data (for debugging)
  getAllStoredData() {
    if (!this.isStorageAvailable()) return {};

    const data = {};
    Object.values(STORAGE_KEYS).forEach(key => {
      data[key] = this.getItem(key);
    });
    return data;
  }

  // Add to StorageService class
    setUnsavedSearchResults(results, timestamp = Date.now()) {
      return this.setItem(STORAGE_KEYS.UNSAVED_SEARCH_RESULTS, {
        results,
        timestamp,
        count: results.length
      });
    }

    getUnsavedSearchResults() {
      const data = this.getItem(STORAGE_KEYS.UNSAVED_SEARCH_RESULTS);
      if (!data) return null;
      
      // Auto-clear after 24 hours
      const twentyFourHours = 24 * 60 * 60 * 1000;
      if (Date.now() - data.timestamp > twentyFourHours) {
        this.removeUnsavedSearchResults();
        return null;
      }
      
      return data;
    }

    removeUnsavedSearchResults() {
      return this.removeItem(STORAGE_KEYS.UNSAVED_SEARCH_RESULTS);
    }
}

export const storageService = new StorageService();
export default StorageService;